import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useOrg } from "./useOrg";

interface EventStreamMessage {
  type: string;
  data: Record<string, unknown>;
  ts: number;
}

type MessageHandler = (msg: EventStreamMessage) => void;

interface EventStreamContextValue {
  subscribe: (channel: string, handler: MessageHandler) => () => void;
  status: "connecting" | "connected" | "disconnected";
}

const EventStreamContext = createContext<EventStreamContextValue>({
  subscribe: () => () => {},
  status: "disconnected",
});

const HEARTBEAT_INTERVAL = 30_000;
const HEARTBEAT_TIMEOUT = 10_000;
const MAX_RECONNECT_DELAY = 30_000;

export function EventStreamProvider({ children }: { children: ReactNode }) {
  const { orgId } = useOrg();
  const [status, setStatus] = useState<"connecting" | "connected" | "disconnected">("disconnected");

  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(new Map<string, Set<MessageHandler>>());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const pongTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  const connect = useCallback(async () => {
    if (!orgId || !mountedRef.current) return;

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus("connecting");

    try {
      if (!mountedRef.current) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = window.location.host;
      const channels = "global,events,deliveries,issues";
      // Same-origin cookies are sent automatically for WebSocket connections
      const url = `${protocol}//${host}/api/ws?channels=${channels}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setStatus("connected");
        reconnectAttemptRef.current = 0;

        // Start heartbeat
        heartbeatTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
            // Set pong timeout
            pongTimerRef.current = setTimeout(() => {
              ws.close(4000, "pong timeout");
            }, HEARTBEAT_TIMEOUT);
          }
        }, HEARTBEAT_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as EventStreamMessage;

          // Handle pong
          if (msg.type === "pong") {
            if (pongTimerRef.current) {
              clearTimeout(pongTimerRef.current);
              pongTimerRef.current = undefined;
            }
            return;
          }

          // Dispatch to all channel handlers
          for (const [channel, handlers] of handlersRef.current) {
            if (channel === "global" || matchesChannel(channel, msg)) {
              for (const handler of handlers) {
                try {
                  handler(msg);
                } catch {
                  // Handler error, continue
                }
              }
            }
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        cleanup();
        if (!mountedRef.current) return;
        setStatus("disconnected");
        scheduleReconnect();
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };
    } catch {
      if (!mountedRef.current) return;
      setStatus("disconnected");
      scheduleReconnect();
    }
  }, [orgId]);

  function cleanup() {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = undefined;
    }
    if (pongTimerRef.current) {
      clearTimeout(pongTimerRef.current);
      pongTimerRef.current = undefined;
    }
  }

  function scheduleReconnect() {
    if (!mountedRef.current) return;
    const delay = Math.min(
      1000 * Math.pow(2, reconnectAttemptRef.current),
      MAX_RECONNECT_DELAY
    );
    reconnectAttemptRef.current++;
    reconnectTimerRef.current = setTimeout(() => {
      connect();
    }, delay);
  }

  // Connect on mount when orgId is available
  useEffect(() => {
    mountedRef.current = true;
    if (orgId) {
      connect();
    }
    return () => {
      mountedRef.current = false;
      cleanup();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [orgId, connect]);

  const subscribe = useCallback(
    (channel: string, handler: MessageHandler): (() => void) => {
      if (!handlersRef.current.has(channel)) {
        handlersRef.current.set(channel, new Set());
      }
      handlersRef.current.get(channel)!.add(handler);

      // If it's a dynamic channel (e.g. collection:xxx), tell the server
      if (channel.includes(":") && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "subscribe", channel }));
      }

      return () => {
        const handlers = handlersRef.current.get(channel);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            handlersRef.current.delete(channel);
            // Unsubscribe from dynamic channels
            if (channel.includes(":") && wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "unsubscribe", channel }));
            }
          }
        }
      };
    },
    []
  );

  return (
    <EventStreamContext.Provider value={{ subscribe, status }}>
      {children}
    </EventStreamContext.Provider>
  );
}

/**
 * Check if a message matches a channel subscription.
 */
function matchesChannel(channel: string, msg: EventStreamMessage): boolean {
  const channelTypeMap: Record<string, string[]> = {
    events: ["event."],
    deliveries: ["delivery."],
    issues: ["issue."],
  };

  const prefixes = channelTypeMap[channel];
  if (prefixes) {
    return prefixes.some((p) => msg.type.startsWith(p));
  }

  // Dynamic channel matching (e.g. "collection:abc123")
  if (channel.startsWith("collection:")) {
    const collectionId = channel.split(":")[1];
    return (
      (msg.type.startsWith("collection.") || msg.type.startsWith("backfill.")) &&
      msg.data.collection_id === collectionId
    );
  }

  return false;
}

export function useEventStream(): EventStreamContextValue {
  return useContext(EventStreamContext);
}
